'use client'

import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { useCallback, useMemo, useState } from 'react'
import { useBridge } from '../bridge/BridgeContext'

export interface UseSliderNavigationParams {
  orderedNodes: ScenarioNodeWithVideoUrl[]
}

export interface UseSliderNavigationReturn {
  currentIndex: number
  isTransitioning: boolean
  slideDirection: 'left' | 'right' | null
  selectedVideoIndex: number | null
  visibleNodes: ScenarioNodeWithVideoUrl[]
  handlePrevious: () => void
  handleNext: () => void
  handleThumbnailClick: (index: number) => void
  handleSPThumbnailClick: (absoluteIndex: number) => void
  handleCloseModal: () => void
  handleRequestOpen: (videoIndex: number) => void
}

export function useSliderNavigation({ orderedNodes }: UseSliderNavigationParams): UseSliderNavigationReturn {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null)

  const visibleNodes = useMemo(
    () =>
      [
        orderedNodes[currentIndex],
        orderedNodes[currentIndex + 1],
        orderedNodes[currentIndex + 2],
      ].filter((node): node is ScenarioNodeWithVideoUrl => node !== undefined),
    [orderedNodes, currentIndex],
  )

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setSlideDirection('right')

    setCurrentIndex((prev) => {
      if (prev === 0) {
        return Math.max(0, orderedNodes.length - 3)
      }
      return prev - 1
    })

    setTimeout(() => {
      setIsTransitioning(false)
      setSlideDirection(null)
    }, 300)
  }, [isTransitioning, orderedNodes.length])

  const handleNext = useCallback(() => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setSlideDirection('left')

    setCurrentIndex((prev) => {
      if (prev >= orderedNodes.length - 3) {
        return 0
      }
      return prev + 1
    })

    setTimeout(() => {
      setIsTransitioning(false)
      setSlideDirection(null)
    }, 300)
  }, [isTransitioning, orderedNodes.length])

  const bridge = useBridge()

  const handleThumbnailClick = useCallback(
    (index: number) => {
      const targetIndex = currentIndex + index
      if (bridge) {
        bridge.sendOpenIntent(orderedNodes[targetIndex]?.videoId)
        return
      }
      setSelectedVideoIndex(targetIndex)
    },
    [bridge, currentIndex, orderedNodes],
  )

  // A7: SP thumbnail click handler (uses absolute index)
  const handleSPThumbnailClick = useCallback(
    (absoluteIndex: number) => {
      if (bridge) {
        bridge.sendOpenIntent(orderedNodes[absoluteIndex]?.videoId)
        return
      }
      setSelectedVideoIndex(absoluteIndex)
    },
    [bridge, orderedNodes],
  )

  const handleCloseModal = useCallback(() => {
    setSelectedVideoIndex(null)
  }, [])

  const handleRequestOpen = useCallback((videoIndex: number) => {
    setSelectedVideoIndex(videoIndex)
  }, [])

  return {
    currentIndex,
    isTransitioning,
    slideDirection,
    selectedVideoIndex,
    visibleNodes,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    handleSPThumbnailClick,
    handleCloseModal,
    handleRequestOpen,
  }
}
